// ============================================
// Book Ride Page — Dual Theme Optimized
// With Interactive Map Route Picker
// ============================================

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import Sidebar from '../../components/common/Sidebar';
import BottomNav from '../../components/common/BottomNav';
import { userMenuItems } from './userNav';
import RouteMapPicker from '../../components/common/RouteMapPicker';
import { useToast } from '../../context/ToastContext';
import { useTheme } from '../../context/ThemeContext';

const BookRide = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const { theme } = useTheme();

    const [loading, setLoading] = useState(false);

    const [formData, setFormData] = useState({
        name: '',
        pickupAddress: '',
        dropoffAddress: '',
        pickupCoords: null,
        dropoffCoords: null,
        scheduledDate: '',
        scheduledTime: '',
        price: '',
        notes: '',
    });

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleMapChange = React.useCallback(({ fromAddress, toAddress, fromCoords, toCoords }) => {
        setFormData((prev) => {
            if (
                prev.pickupAddress === fromAddress &&
                prev.dropoffAddress === toAddress &&
                JSON.stringify(prev.pickupCoords) === JSON.stringify(fromCoords) &&
                JSON.stringify(prev.dropoffCoords) === JSON.stringify(toCoords)
            ) {
                return prev;
            }
            return {
                ...prev,
                pickupAddress: fromAddress || prev.pickupAddress,
                dropoffAddress: toAddress || prev.dropoffAddress,
                pickupCoords: fromCoords || prev.pickupCoords,
                dropoffCoords: toCoords || prev.dropoffCoords,
            };
        });
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            await api.post('/user/bookings', formData);
            toast.success('Booking request sent successfully!');
            setTimeout(() => navigate('/user/trips'), 2000);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to create booking');
        } finally {
            setLoading(false);
        }
    };

    const routeReady = formData.pickupAddress && formData.dropoffAddress;

    return (
        <div className="flex h-screen bg-slate-50 dark:bg-[#050510] text-slate-900 dark:text-white font-['Inter',sans-serif] transition-colors duration-500 aurora-bg relative">
            <Sidebar items={userMenuItems} theme="teal" title="IdaCab Public" />

            <div className="flex-1 flex flex-col overflow-hidden">
                {/* ── Premium Hyper-Glass Header ── */}
                <header className="h-16 sm:h-24 bg-white/40 dark:bg-slate-900/40 backdrop-blur-3xl flex items-center justify-between px-5 sm:px-10 sticky top-0 z-50 border-b border-white/60 dark:border-white/5 shadow-sm dark:shadow-2xl transition-colors duration-500">
                    <div>
                        <h2 className="text-lg sm:text-2xl font-black tracking-tighter text-slate-800 dark:text-white leading-tight mb-1">Book a <span className="bg-gradient-to-r from-teal-500 to-cyan-500 bg-clip-text text-transparent">New Ride</span></h2>
                        <p className="text-[10px] sm:text-[11px] font-bold text-slate-500 dark:text-slate-400 tracking-[0.1em] uppercase leading-none mt-0.5">Search your route and book instantly</p>
                    </div>
                </header>

                {/* Content */}
                <main className="flex-1 overflow-y-auto p-4 sm:p-8 pb-24 sm:pb-12 bg-transparent sidebar-scroll transition-colors duration-500">
                    <div className="max-w-3xl mx-auto">
                        <div className="relative bg-white/70 dark:bg-slate-900/60 backdrop-blur-3xl rounded-[2.5rem] border border-white/60 dark:border-white/10 shadow-xl shadow-slate-200/50 dark:shadow-black/50 overflow-hidden transition-all duration-500 hover:shadow-2xl hover:shadow-teal-500/20 dark:hover:shadow-teal-500/10 group">
                            <div className="absolute inset-0 bg-gradient-to-br from-teal-500/5 via-transparent to-cyan-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none z-0"></div>
                            <div className="relative z-10 w-full h-full">
                                {/* Card Header */}
                                <div className="bg-white/40 dark:bg-slate-800/20 p-6 sm:p-8 flex items-center justify-between border-b border-white/60 dark:border-white/5 transition-colors">
                                <div>
                                    <h3 className="font-black tracking-tight text-xl sm:text-2xl text-slate-800 dark:text-white mb-1">Select Your Route</h3>
                                    <p className="text-slate-500 dark:text-slate-400 text-[11px] font-bold uppercase tracking-[0.1em]">Use the map to pick your exact pickup and drop-off</p>
                                </div>
                                {routeReady && (
                                    <span className="text-[10px] text-teal-600 dark:text-teal-400 font-bold flex items-center gap-1.5 bg-teal-50 dark:bg-teal-500/10 border border-teal-200 dark:border-teal-500/20 px-3 py-1.5 rounded-full uppercase tracking-widest hidden sm:flex">
                                        <div className="w-1.5 h-1.5 bg-teal-500 dark:bg-teal-400 rounded-full animate-pulse" />
                                        Route Set
                                    </span>
                                )}
                            </div>

                            {/* ── Step 1: Interactive Map Route Picker ── */}
                            <div className="p-0 sm:p-8 bg-transparent transition-colors">
                                <div className="rounded-[2.5rem] overflow-hidden border border-white/60 dark:border-white/5 shadow-inner">
                                    <RouteMapPicker
                                        fromAddress={formData.pickupAddress}
                                        toAddress={formData.dropoffAddress}
                                        fromCoords={formData.pickupCoords}
                                        toCoords={formData.dropoffCoords}
                                        onChange={handleMapChange}
                                        height={window.innerWidth < 640 ? "280px" : "360px"}
                                        theme="teal"
                                        dark={theme === 'dark'}
                                    />
                                </div>
                            </div>

                            {/* ── Step 2: Trip Details Form ── */}
                            <form onSubmit={handleSubmit} className="p-6 sm:p-8 space-y-6 pt-8 sm:pt-4 border-t border-white/60 dark:border-white/5 transition-colors">
                                <div className="flex items-center gap-3 mb-4">
                                    <span className="w-8 h-8 rounded-xl bg-gradient-to-br from-teal-400 to-teal-600 text-white text-xs font-black flex items-center justify-center shadow-lg shadow-teal-500/20 transition-colors">2</span>
                                    <h4 className="text-[13px] font-black text-slate-800 dark:text-white uppercase tracking-[0.2em]">Trip Details</h4>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-8">
                                    {/* Trip Name */}
                                    <div className="space-y-2 lg:col-span-2">
                                        <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 dark:text-slate-400 ml-2">Trip Name</label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            required
                                            placeholder="e.g. Airport Transfer"
                                            className="w-full px-6 py-4 rounded-[1.5rem] bg-white/70 dark:bg-slate-800/50 backdrop-blur-md border border-white/60 dark:border-white/10 focus:border-teal-500 focus:ring-4 focus:ring-teal-500/10 transition-all outline-none text-slate-800 dark:text-white text-[13px] font-black placeholder:text-slate-400 shadow-inner hover:bg-white dark:hover:bg-slate-800/80"
                                        />
                                    </div>

                                    {/* Pickup Address */}
                                    <div className="space-y-2 lg:col-span-2">
                                        <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 dark:text-slate-400 ml-2 flex items-center gap-2">
                                            <span className="w-2.5 h-2.5 rounded-full bg-teal-400 shadow-[0_0_8px_rgba(45,212,191,0.6)]" />
                                            Pickup Address
                                        </label>
                                        <input
                                            type="text"
                                            name="pickupAddress"
                                            value={formData.pickupAddress}
                                            onChange={handleChange}
                                            required
                                            placeholder="Selected from map, or search manually"
                                            className="w-full px-6 py-4 rounded-[1.5rem] bg-white/70 dark:bg-slate-800/50 backdrop-blur-md border border-white/60 dark:border-white/10 focus:border-teal-500 focus:ring-4 focus:ring-teal-500/10 transition-all outline-none text-slate-800 dark:text-white text-[13px] font-black placeholder:text-slate-400 shadow-inner hover:bg-white dark:hover:bg-slate-800/80"
                                        />
                                    </div>

                                    {/* Dropoff Address */}
                                    <div className="space-y-2 lg:col-span-2">
                                        <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 dark:text-slate-400 ml-2 flex items-center gap-2">
                                            <span className="w-2.5 h-2.5 rounded-full bg-rose-400 shadow-[0_0_8px_rgba(251,113,133,0.6)]" />
                                            Dropoff Address
                                        </label>
                                        <input
                                            type="text"
                                            name="dropoffAddress"
                                            value={formData.dropoffAddress}
                                            onChange={handleChange}
                                            required
                                            placeholder="Selected from map, or search manually"
                                            className="w-full px-6 py-4 rounded-[1.5rem] bg-white/70 dark:bg-slate-800/50 backdrop-blur-md border border-white/60 dark:border-rose-500/30 focus:border-rose-500 focus:ring-4 focus:ring-rose-500/10 transition-all outline-none text-slate-800 dark:text-white text-[13px] font-black placeholder:text-slate-400 shadow-inner hover:bg-white dark:hover:bg-slate-800/80"
                                        />
                                    </div>

                                    {/* Date */}
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 dark:text-slate-400 ml-2">Scheduled Date</label>
                                        <input
                                            type="date"
                                            name="scheduledDate"
                                            value={formData.scheduledDate}
                                            onChange={handleChange}
                                            required
                                            className="w-full px-6 py-4 rounded-[1.5rem] bg-white/70 dark:bg-slate-800/50 backdrop-blur-md border border-white/60 dark:border-white/10 focus:border-teal-500 focus:ring-4 focus:ring-teal-500/10 transition-all outline-none text-slate-800 dark:text-white text-[13px] font-black dark:color-scheme-dark shadow-inner hover:bg-white dark:hover:bg-slate-800/80"
                                        />
                                    </div>

                                    {/* Time */}
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 dark:text-slate-400 ml-2">Scheduled Time</label>
                                        <input
                                            type="time"
                                            name="scheduledTime"
                                            value={formData.scheduledTime}
                                            onChange={handleChange}
                                            required
                                            className="w-full px-6 py-4 rounded-[1.5rem] bg-white/70 dark:bg-slate-800/50 backdrop-blur-md border border-white/60 dark:border-white/10 focus:border-teal-500 focus:ring-4 focus:ring-teal-500/10 transition-all outline-none text-slate-800 dark:text-white text-[13px] font-black dark:color-scheme-dark shadow-inner hover:bg-white dark:hover:bg-slate-800/80"
                                        />
                                    </div>

                                    {/* Budget */}
                                    <div className="space-y-2 lg:col-span-2">
                                        <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 dark:text-slate-400 ml-2">Budget / Fare Offer (GBP)</label>
                                        <div className="relative group/input">
                                            <span className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-500 dark:text-slate-400 font-black text-lg">£</span>
                                            <input
                                                type="number"
                                                name="price"
                                                value={formData.price}
                                                onChange={handleChange}
                                                required
                                                placeholder="0.00"
                                                className="w-full pl-12 pr-6 py-4 rounded-[1.5rem] bg-white/70 dark:bg-slate-800/50 backdrop-blur-md border border-white/60 dark:border-white/10 focus:border-teal-500 focus:ring-4 focus:ring-teal-500/10 transition-all outline-none text-slate-800 dark:text-white font-black text-[15px] placeholder:text-slate-400 shadow-inner hover:bg-white dark:hover:bg-slate-800/80"
                                            />
                                        </div>
                                    </div>

                                    {/* Notes */}
                                    <div className="space-y-2 lg:col-span-2">
                                        <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 dark:text-slate-400 ml-2">Additional Notes</label>
                                        <textarea
                                            name="notes"
                                            value={formData.notes}
                                            onChange={handleChange}
                                            placeholder="Flight number, luggage size, child seats, etc."
                                            rows="3"
                                            className="w-full px-6 py-4 rounded-[1.5rem] bg-white/70 dark:bg-slate-800/50 backdrop-blur-md border border-white/60 dark:border-white/10 focus:border-teal-500 focus:ring-4 focus:ring-teal-500/10 transition-all outline-none text-slate-800 dark:text-white text-[13px] font-black resize-none placeholder:text-slate-400 shadow-inner hover:bg-white dark:hover:bg-slate-800/80"
                                        />
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    disabled={loading || !routeReady}
                                    className={`w-full py-5 rounded-[2rem] bg-gradient-to-r from-teal-500 to-cyan-600 text-white font-black text-sm sm:text-base uppercase tracking-[0.2em] transition-all shadow-xl shadow-teal-500/30 hover:shadow-teal-500/50 flex items-center justify-center gap-3 hover:-translate-y-1 active:translate-y-0 active:scale-[0.98] mt-6 ${loading || !routeReady ? 'opacity-50 cursor-not-allowed grayscale shadow-none hover:translate-y-0' : ''}`}
                                >
                                    {loading ? (
                                        <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    ) : (
                                        <>
                                            <span>Confirm Booking Request</span>
                                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                            </svg>
                                        </>
                                    )}
                                </button>
                            </form>
                            </div>
                        </div>
                    </div>
                </main>
            </div>
            <BottomNav items={userMenuItems} />
            <style>{`.dark\\:color-scheme-dark { color-scheme: ${theme === 'dark' ? 'dark' : 'light'}; }`}</style>
        </div>
    );
};

export default BookRide;
